import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Zap, Crown, Star, ArrowRight, Loader, X, Clock, Info } from 'lucide-react';
import { useUser } from '../contexts/UserContext';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { APP_CONFIG, SUBSCRIPTION_CONSTANTS, IS_STRIPE_TEST_MODE } from '../config/appConfig';
import { useToast } from '../contexts/ToastContext';
import logger from '../utils/logger';

const SelectPlan = () => {
  const navigate = useNavigate();
  const { user, userSubscription, updateSubscription, refreshSubscription, loading: userLoading } = useUser();

  const [billingCycle, setBillingCycle] = useState(userSubscription?.billingCycle || 'monthly');
  const [processing, setProcessing] = useState(null);
  const [changeConfirm, setChangeConfirm] = useState(null); // { planName, priceId, planId, preview, success, ... }
  const [cancelConfirm, setCancelConfirm] = useState(null);
  const { success: showSuccess, error: showError } = useToast();

  useEffect(() => {
    if (userSubscription?.billingCycle) setBillingCycle(userSubscription.billingCycle);
  }, [userSubscription]);

  if (userLoading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-green-500 mx-auto mb-4"></div>
          <p className="text-green-500 font-bold">Loading plans...</p>
        </div>
      </div>
    );
  }

  if (!user) { navigate('/login'); return null; }

  // ── Helpers ──
  const formatDate = (d) => {
    if (!d) return '';
    const date = d?.seconds ? new Date(d.seconds * 1000) : new Date(d);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const hasScheduledChange = !!userSubscription?.scheduledPlanChange;
  const scheduledPlan = hasScheduledChange ? SUBSCRIPTION_CONSTANTS.getPlanByDbId(userSubscription.scheduledPlanChange) : null;

  // ── Button state per card ──
  const getButtonState = (planDbId) => {
    const currentPlan = userSubscription?.subscriptionPlan;
    const currentCycle = userSubscription?.billingCycle || 'monthly';
    const currentStatus = userSubscription?.subscriptionStatus;
    const isCancelled = currentStatus === 'cancelled';
    const isActive = currentStatus === 'active';
    const planConfig = Object.values(SUBSCRIPTION_CONSTANTS.PLAN_DETAILS).find(p => p.dbId === planDbId);
    if (!planConfig) return { text: 'Subscribe Now', disabled: false };

    // Basic — always default
    if (planDbId === 'basic') {
      if ((currentPlan === 'trackaipro' || currentPlan === 'cricketxpro') && (isActive || isCancelled)) return { text: 'Included', disabled: true };
      return { text: 'Current Plan', disabled: true };
    }

    // Scheduled change target
    if (hasScheduledChange && userSubscription.scheduledPlanChange === planDbId) {
      return { text: 'Change Pending', disabled: true };
    }

    const isSamePlan = currentPlan === planDbId;
    const isSameCycle = currentCycle === billingCycle;

    if (isSamePlan) {
      if (isCancelled) return { text: `Re-subscribe`, disabled: false };
      if (isActive && isSameCycle) return { text: 'Current Plan', disabled: true };
      if (isActive && !isSameCycle) return { text: billingCycle === 'yearly' ? 'Upgrade to Yearly' : 'Switch to Monthly', disabled: false };
    }

    if (planDbId === 'cricketxpro') {
      if (currentPlan === 'trackaipro' && isActive) return { text: 'Upgrade to VIP Pass', disabled: false };
      if (currentPlan === 'trackaipro' && isCancelled) return { text: 'Subscribe to VIP Pass', disabled: false };
    }

    if (planDbId === 'trackaipro') {
      if (currentPlan === 'cricketxpro' && isActive) return { text: 'Downgrade to Elite', disabled: false };
      if (currentPlan === 'cricketxpro' && isCancelled) return { text: 'Subscribe to Elite', disabled: false };
    }

    if (currentPlan === 'basic' || !currentPlan) return { text: `Subscribe to ${planConfig.name}`, disabled: false };
    return { text: `Subscribe to ${planConfig.name}`, disabled: false };
  };

  // ── Handle plan action ──
  const handlePlanAction = async (planDbId) => {
    const planConfig = Object.values(SUBSCRIPTION_CONSTANTS.PLAN_DETAILS).find(p => p.dbId === planDbId);
    if (!planConfig) return;

    // Free plan → cancel flow
    if (planConfig.isFree) {
      const hasActiveSub = userSubscription?.stripeSubscriptionId && userSubscription?.subscriptionStatus === 'active';
      if (hasActiveSub && userSubscription?.subscriptionPlan !== 'basic') {
        setCancelConfirm({ show: true });
        return;
      }
      setProcessing(planDbId);
      try {
        await updateSubscription(planConfig.id, 'inactive', { billingCycle: 'monthly' });
        showSuccess('Plan updated to Basic successfully!');
      } catch (error) { showError('Failed to update plan. Please try again.'); }
      setProcessing(null);
      return;
    }

    const isYearly = billingCycle === 'yearly';
    const targetPriceId = isYearly ? planConfig.priceIdYearly : planConfig.priceId;
    const stripePlanId = isYearly ? (planConfig.idYearly || planConfig.dbId) : planConfig.dbId;
    const planName = planConfig.name;
    const isCancelled = userSubscription?.subscriptionStatus === 'cancelled';
    const isSamePlan = userSubscription?.subscriptionPlan === planDbId;

    setProcessing(planDbId);

    try {
      // Cancelled + same plan → re-activate (free)
      if (isCancelled && isSamePlan && userSubscription?.billingCycle === billingCycle) {
        const functions = getFunctions();
        const reactivateFn = httpsCallable(functions, 'reactivateSubscription');
        await reactivateFn({ isTestMode: IS_STRIPE_TEST_MODE || false });
        await refreshSubscription();
        showSuccess('Subscription reactivated! Billing resumes on the next cycle.');
        setProcessing(null);
        return;
      }

      // Active subscription → preview + confirm
      if (userSubscription?.stripeSubscriptionId && userSubscription?.subscriptionStatus === 'active') {
        const functions = getFunctions();
        const previewFn = httpsCallable(functions, 'previewPlanChange');
        const preview = await previewFn({
          newPriceId: targetPriceId,
          newPlanId: stripePlanId,
          isTestMode: IS_STRIPE_TEST_MODE || false,
        });
        setChangeConfirm({ planName, priceId: targetPriceId, planId: stripePlanId, preview: preview.data });
        setProcessing(null);
        return;
      }

      // No active subscription → new checkout
      const functions = getFunctions();
      const createCheckoutSession = httpsCallable(functions, 'createCheckoutSession');
      const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      const returnUrl = isLocalhost ? `${window.location.origin}/trackai` : undefined;

      const result = await createCheckoutSession({
        priceId: targetPriceId, planId: stripePlanId, userId: user.uid, returnUrl,
        isTestMode: IS_STRIPE_TEST_MODE || false,
      });

      if (result.data.checkoutUrl) {
        window.location.href = result.data.checkoutUrl;
      } else {
        throw new Error('No checkout URL returned from server');
      }
    } catch (error) {
      logger.error('SelectPlan', 'Error:', error);
      showError(`${SUBSCRIPTION_CONSTANTS.MESSAGES.UPDATE_ERROR}: ${error.message || 'Please try again.'}`);
      setProcessing(null);
    }
  };

  // ── Confirm plan change (after preview) ──
  const handleConfirmChange = async () => {
    if (!changeConfirm) return;
    setProcessing('confirming');
    try {
      const functions = getFunctions();
      const updateSubscriptionFn = httpsCallable(functions, 'updateSubscription');
      const result = await updateSubscriptionFn({
        newPriceId: changeConfirm.priceId,
        newPlanId: changeConfirm.planId,
        isTestMode: IS_STRIPE_TEST_MODE || false,
      });
      if (result.data.success) {
        await refreshSubscription();
        setChangeConfirm({
          ...changeConfirm,
          success: true,
          immediate: result.data.immediate,
          dbPlan: result.data.dbPlan,
          cycle: result.data.billingCycle,
          effectiveDate: result.data.effectiveDate,
        });
      }
    } catch (error) {
      logger.error('SelectPlan', 'Error confirming change:', error);
      showError(error.message || 'Failed to update plan. Please try again.');
      setChangeConfirm(null);
    } finally {
      setProcessing(null);
    }
  };

  // ── Cancel scheduled change ──
  const handleCancelScheduledChange = async () => {
    setProcessing('cancel-schedule');
    try {
      const functions = getFunctions();
      const cancelScheduleFn = httpsCallable(functions, 'cancelScheduledChange');
      await cancelScheduleFn({ isTestMode: IS_STRIPE_TEST_MODE || false });
      await refreshSubscription();
      showSuccess('Scheduled plan change cancelled. Your current plan continues.');
    } catch (error) {
      logger.error('SelectPlan', 'Error cancelling schedule:', error);
      showError('Failed to cancel scheduled change. Please try again.');
    } finally {
      setProcessing(null);
    }
  };

  // ── Cancel subscription ──
  const handleCancelSubscription = async () => {
    setProcessing('cancel');
    try {
      if (userSubscription?.stripeSubscriptionId) {
        const functions = getFunctions();
        const cancelFn = httpsCallable(functions, 'cancelSubscription');
        await cancelFn({ subscriptionId: userSubscription.stripeSubscriptionId, isTestMode: IS_STRIPE_TEST_MODE || false });
        setCancelConfirm({ success: true });
      } else {
        await updateSubscription('basic', 'inactive');
        setCancelConfirm({ success: true });
      }
    } catch (error) {
      logger.error('SelectPlan', 'Error cancelling:', error);
      showError('Failed to cancel subscription. Please contact support.');
      setCancelConfirm(null);
    } finally {
      setProcessing(null);
    }
  };

  const getPlanIcon = (id) => ({ basic: Zap, trackaipro: Star, cricketxpro: Crown }[id] || Zap);

  return (
    <div className="min-h-screen bg-gray-900 py-12 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-extrabold text-white mb-4">Choose Your Training Plan</h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto mb-8">Select the perfect plan to elevate your cricket game.</p>

          {/* Cancelled Notice */}
          {userSubscription?.subscriptionStatus === 'cancelled' && userSubscription?.subscriptionPlan !== 'basic' && (
            <div className="mb-8 bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 max-w-2xl mx-auto text-left">
              <div className="flex items-start gap-3">
                <X size={20} className="text-yellow-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-yellow-300 font-bold text-sm">Subscription Cancelled</p>
                  <p className="text-gray-400 text-sm mt-1">
                    Your {SUBSCRIPTION_CONSTANTS.getDisplayName(userSubscription.subscriptionPlan)} has been cancelled.
                    {userSubscription.currentPeriodEnd && (<> Access until <span className="text-yellow-400 font-semibold">{formatDate(userSubscription.currentPeriodEnd)}</span>.</>)}
                    {' '}Re-subscribe below to continue uninterrupted.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Scheduled Change Notice */}
          {hasScheduledChange && (
            <div className="mb-8 bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 max-w-2xl mx-auto text-left">
              <div className="flex items-start gap-3">
                <Clock size={20} className="text-blue-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-blue-300 font-bold text-sm">Plan Change Scheduled</p>
                  <p className="text-gray-400 text-sm mt-1">
                    Your plan will change to <span className="text-blue-400 font-semibold">{scheduledPlan?.name || userSubscription.scheduledPlanChange}</span> ({userSubscription.scheduledBillingCycle}) on <span className="text-blue-400 font-semibold">{formatDate(userSubscription.scheduledChangeDate)}</span>.
                    You keep all current features until then.
                  </p>
                </div>
                <button
                  onClick={handleCancelScheduledChange}
                  disabled={processing === 'cancel-schedule'}
                  className="bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 font-bold py-1.5 px-4 rounded-lg text-xs border border-blue-500/30 whitespace-nowrap"
                >
                  {processing === 'cancel-schedule' ? 'Cancelling...' : 'Cancel Change'}
                </button>
              </div>
            </div>
          )}

          {/* Billing Toggle */}
          <div className="inline-flex bg-gray-800 p-2 rounded-full border border-gray-700 relative scale-110 shadow-lg">
            <button onClick={() => setBillingCycle('monthly')} className={`px-10 py-3 rounded-full text-base font-bold transition-all ${billingCycle === 'monthly' ? 'bg-green-600 text-white shadow-lg' : 'text-gray-400 hover:text-white'}`}>Monthly</button>
            <button onClick={() => setBillingCycle('yearly')} className={`px-10 py-3 rounded-full text-base font-bold transition-all flex items-center gap-2 ${billingCycle === 'yearly' ? 'bg-green-600 text-white shadow-lg' : 'text-gray-400 hover:text-white'}`}>
              Yearly <span className="bg-yellow-500 text-black text-[11px] px-2 py-0.5 rounded-full uppercase tracking-tighter font-bold">Best Value</span>
            </button>
          </div>
        </div>

        {/* Plans Grid */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          {Object.values(SUBSCRIPTION_CONSTANTS.PLAN_DETAILS).map((plan) => {
            const Icon = getPlanIcon(plan.dbId);
            const btnState = getButtonState(plan.dbId);
            const isCurrentPlan = userSubscription?.subscriptionPlan === plan.dbId;
            const isActiveSub = isCurrentPlan && userSubscription?.subscriptionStatus === 'active' && userSubscription?.billingCycle === billingCycle;
            const isCancelledPlan = isCurrentPlan && userSubscription?.subscriptionStatus === 'cancelled';
            const isScheduledTarget = hasScheduledChange && userSubscription.scheduledPlanChange === plan.dbId;

            const displayPrice = (billingCycle === 'yearly' && plan.priceYearly !== undefined) ? plan.priceYearly : plan.price;
            const features = (billingCycle === 'yearly' && plan.featuresYearly) ? plan.featuresYearly : plan.features;

            return (
              <div key={plan.id} className={`relative bg-gray-800 rounded-2xl p-6 border-2 transition-all ${isActiveSub ? 'border-green-500 shadow-xl shadow-green-500/20' : isCancelledPlan ? 'border-yellow-500/50 shadow-lg shadow-yellow-500/10' : isScheduledTarget ? 'border-blue-500/50 shadow-lg shadow-blue-500/10' : 'border-gray-700 hover:border-gray-600'} ${plan.recommended ? 'ring-2 ring-green-500' : ''}`}>
                {plan.recommended && <div className="absolute -top-3 left-1/2 transform -translate-x-1/2"><span className="bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-full uppercase">Most Popular</span></div>}
                {isActiveSub && <div className="absolute top-2 right-2"><span className="bg-blue-500/20 text-blue-400 text-[10px] font-bold px-2 py-0.5 rounded border border-blue-500/30 uppercase">Current</span></div>}
                {isCancelledPlan && !plan.isFree && <div className="absolute top-2 right-2"><span className="bg-yellow-500/20 text-yellow-400 text-[10px] font-bold px-2 py-0.5 rounded border border-yellow-500/30 uppercase">Cancelled</span></div>}
                {isScheduledTarget && <div className="absolute top-2 right-2"><span className="bg-blue-500/20 text-blue-400 text-[10px] font-bold px-2 py-0.5 rounded border border-blue-500/30 uppercase">Scheduled</span></div>}

                <div className="text-center mb-6">
                  <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 ${plan.dbId === 'basic' ? 'bg-blue-500/20' : plan.dbId === 'trackaipro' ? 'bg-green-500/20' : 'bg-purple-500/20'}`}>
                    <Icon size={28} className={plan.dbId === 'basic' ? 'text-blue-400' : plan.dbId === 'trackaipro' ? 'text-green-400' : 'text-purple-400'} />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-1">{plan.name}</h3>
                  {plan.dbId === 'trackaipro' && <p className="text-sm text-gray-400 mb-2">Web + Mobile App</p>}
                  {plan.dbId === 'cricketxpro' && <p className="text-sm text-gray-400 mb-2">Meta App & Mobile App</p>}
                  <div className="flex items-baseline justify-center">
                    {plan.isFree ? <span className="text-4xl font-extrabold text-green-400">Free</span> : <><span className="text-4xl font-extrabold text-white">${displayPrice}</span><span className="text-gray-400 ml-2">/{billingCycle === 'yearly' ? 'year' : 'month'}</span></>}
                  </div>
                  {billingCycle === 'yearly' && !plan.isFree && <p className="text-green-400 text-sm mt-2 font-semibold">Save ${(plan.price * 12) - plan.priceYearly} per year</p>}
                </div>

                {plan.dbId === 'cricketxpro' && (<>
                  {billingCycle === 'yearly' && <div className="bg-gradient-to-r from-yellow-500/20 to-yellow-600/20 border border-yellow-500/50 p-3 rounded-xl mb-3"><p className="text-yellow-400 font-bold text-sm text-center">FREE VR Bat Included (Yearly Exclusive)</p></div>}
                  <div className="bg-green-500/20 border border-green-500/30 p-3 rounded-xl mb-3"><p className="text-green-400 font-bold text-sm text-center">Includes TrackAI Elite FREE</p></div>
                  <div className="bg-yellow-400/10 border border-yellow-400/30 p-2 rounded-xl mb-3 text-center"><p className="text-[11px] font-bold flex items-center justify-center gap-1 text-yellow-400 uppercase tracking-tighter">Requires CricketX Base Game</p><p className="text-[10px] text-gray-400">Purchased separately on Meta Store</p></div>
                </>)}

                <ul className="space-y-3 mb-6">
                  {features.map((f, i) => <li key={i} className="flex items-start text-gray-300"><Check size={18} className="text-green-500 mr-2 mt-0.5 flex-shrink-0" /><span className="text-sm">{f}</span></li>)}
                </ul>

                <button onClick={() => !btnState.disabled && handlePlanAction(plan.dbId)} disabled={btnState.disabled || !!processing}
                  className={`w-full font-bold py-3 px-6 rounded-lg text-base transition-all flex items-center justify-center gap-2 ${btnState.disabled ? 'bg-gray-700/50 text-gray-500 cursor-not-allowed' : plan.dbId === 'cricketxpro' ? 'bg-purple-600 hover:bg-purple-500 text-white' : plan.dbId === 'trackaipro' ? 'bg-green-600 hover:bg-green-500 text-white' : 'bg-gray-700 hover:bg-gray-600 text-white border border-gray-600'}`}>
                  {processing === plan.dbId ? <><Loader className="animate-spin h-5 w-5" /> Processing...</> : <>{btnState.text}{!btnState.disabled && <ArrowRight size={18} />}</>}
                </button>
              </div>
            );
          })}
        </div>

        {/* Cancel Subscription */}
        {userSubscription?.subscriptionStatus === 'active' && userSubscription?.subscriptionPlan !== 'basic' && (
          <div className="bg-gray-800/50 rounded-xl p-5 border border-red-500/20 flex flex-col sm:flex-row items-center justify-between gap-4 mb-8">
            <div>
              <h3 className="text-white font-bold">Want to cancel?</h3>
              <p className="text-gray-400 text-sm">Your access continues until the end of the current billing period.</p>
            </div>
            <button onClick={() => setCancelConfirm({ show: true })} className="bg-red-500/20 hover:bg-red-500/30 text-red-400 font-bold py-2 px-6 rounded-lg transition-all border border-red-500/30 text-sm whitespace-nowrap">Cancel Subscription</button>
          </div>
        )}

        {/* Trust Badges */}
        <div className="pt-8 border-t border-gray-800">
          <div className="flex flex-wrap justify-center items-center gap-8 text-gray-500 text-sm">
            <div className="flex items-center"><Check size={16} className="text-green-500 mr-2" />Secure Payment</div>
            <div className="flex items-center"><Check size={16} className="text-green-500 mr-2" />No Hidden Fees</div>
            <div className="flex items-center"><Check size={16} className="text-green-500 mr-2" />Cancel Anytime</div>
            <div className="flex items-center"><Check size={16} className="text-green-500 mr-2" />7-Day Money-Back Guarantee</div>
          </div>
        </div>
      </div>

      {/* ── Plan Change Confirmation Modal (with proration preview) ── */}
      {changeConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="bg-gray-800 border border-gray-700 rounded-2xl p-8 max-w-md w-full shadow-2xl">
            {changeConfirm.success ? (
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-green-500/20 rounded-full mb-6"><Check size={32} className="text-green-400" /></div>
                <h3 className="text-2xl font-bold text-white mb-3">
                  {changeConfirm.immediate ? "You're all set!" : 'Change Scheduled!'}
                </h3>
                <p className="text-gray-300 mb-2">
                  {changeConfirm.immediate ? (
                    <>Your plan has been changed to <span className="text-green-400 font-bold">{changeConfirm.planName}</span> ({changeConfirm.cycle}). Your new features are available right now.</>
                  ) : (
                    <>Done! Your plan will automatically switch to <span className="text-blue-400 font-bold">{changeConfirm.planName}</span> ({changeConfirm.cycle}) on <span className="text-blue-400 font-bold">{formatDate(changeConfirm.effectiveDate)}</span>. Nothing changes until then — enjoy your current plan in the meantime.</>
                  )}
                </p>
                <button onClick={() => setChangeConfirm(null)} className="w-full mt-4 bg-green-600 hover:bg-green-500 text-white font-bold py-3 px-6 rounded-lg transition-all">Done</button>
              </div>
            ) : changeConfirm.preview?.immediate ? (
              /* ── Immediate upgrade modal with proration breakdown ── */
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-green-500/20 rounded-full mb-6"><ArrowRight size={32} className="text-green-400" /></div>
                <h3 className="text-2xl font-bold text-white mb-3">Upgrade to {changeConfirm.planName}</h3>
                <p className="text-gray-400 text-sm mb-4">({billingCycle})</p>

                {/* Proration breakdown */}
                <div className="bg-gray-900 border border-gray-700 rounded-xl p-4 mb-6 text-sm">
                  <div className="flex justify-between text-gray-300 mb-2">
                    <span>{changeConfirm.planName} ({billingCycle})</span>
                    <span className="font-bold">${changeConfirm.preview.charge.toFixed(2)}</span>
                  </div>
                  {changeConfirm.preview.credit < 0 && (
                    <div className="flex justify-between text-gray-300 mb-2">
                      <span>Credit applied</span>
                      <span className="text-green-400 font-bold">-${Math.abs(changeConfirm.preview.credit).toFixed(2)}</span>
                    </div>
                  )}
                  <div className="border-t border-gray-700 pt-2 mt-2 flex justify-between text-white font-bold">
                    <span>Total charged now</span>
                    <span className="text-green-400">${changeConfirm.preview.netAmount.toFixed(2)}</span>
                  </div>
                </div>

                <p className="text-gray-400 text-xs mb-6">
                  <Info size={12} className="inline mr-1" />
                  {changeConfirm.preview.netAmount <= 0
                    ? 'You have enough credit — no charge this cycle.'
                    : `You'll be charged $${changeConfirm.preview.netAmount.toFixed(2)} now.`}
                  {' '}Features activate immediately.
                </p>

                <div className="flex gap-3">
                  <button onClick={() => setChangeConfirm(null)} className="flex-1 bg-gray-700 hover:bg-gray-600 text-white font-bold py-3 px-6 rounded-lg transition-all border border-gray-600">Cancel</button>
                  <button onClick={handleConfirmChange} disabled={processing === 'confirming'} className="flex-1 bg-green-600 hover:bg-green-500 text-white font-bold py-3 px-6 rounded-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50">
                    {processing === 'confirming' ? <><Loader className="animate-spin h-5 w-5" /> Updating...</> : (
                      changeConfirm.preview.netAmount > 0 ? `Confirm & Pay $${changeConfirm.preview.netAmount.toFixed(2)}` : 'Confirm Change'
                    )}
                  </button>
                </div>
              </div>
            ) : (
              /* ── Deferred downgrade modal ── */
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-500/20 rounded-full mb-6"><Clock size={32} className="text-blue-400" /></div>
                <h3 className="text-2xl font-bold text-white mb-3">Switch to {changeConfirm.planName}</h3>
                <p className="text-gray-400 text-sm mb-2">({billingCycle})</p>
                <p className="text-gray-300 text-sm mb-6">
                  Since you've already paid for your current plan{changeConfirm.preview?.effectiveDate ? <> through <span className="text-blue-400 font-semibold">{formatDate(changeConfirm.preview.effectiveDate)}</span></> : ''}, we'll switch you to {changeConfirm.planName} after your current billing period ends so you don't lose any access you've paid for.
                </p>

                <div className="bg-gray-900 border border-gray-700 rounded-xl p-4 mb-6 text-left text-sm space-y-3">
                  <div className="flex items-start gap-2 text-gray-300">
                    <Check size={16} className="text-blue-400 mt-0.5 flex-shrink-0" />
                    <span>Keep all your current features until {changeConfirm.preview?.effectiveDate ? <span className="text-blue-400 font-bold">{formatDate(changeConfirm.preview.effectiveDate)}</span> : 'your current billing period ends'}</span>
                  </div>
                  <div className="flex items-start gap-2 text-gray-300">
                    <Check size={16} className="text-blue-400 mt-0.5 flex-shrink-0" />
                    <span><span className="text-white font-semibold">{changeConfirm.planName}</span> ({billingCycle}) kicks in automatically after that</span>
                  </div>
                  <div className="flex items-start gap-2 text-gray-300">
                    <Check size={16} className="text-blue-400 mt-0.5 flex-shrink-0" />
                    <span>Nothing to pay right now — you can cancel this change before it happens</span>
                  </div>
                </div>

                <div className="flex gap-3">
                  <button onClick={() => setChangeConfirm(null)} className="flex-1 bg-gray-700 hover:bg-gray-600 text-white font-bold py-3 px-6 rounded-lg transition-all border border-gray-600">Keep Current Plan</button>
                  <button onClick={handleConfirmChange} disabled={processing === 'confirming'} className="flex-1 bg-blue-600 hover:bg-blue-500 text-white font-bold py-3 px-6 rounded-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50">
                    {processing === 'confirming' ? <><Loader className="animate-spin h-5 w-5" /> Scheduling...</> : 'Schedule Switch'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Cancel Subscription Modal ── */}
      {cancelConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="bg-gray-800 border border-gray-700 rounded-2xl p-8 max-w-md w-full shadow-2xl">
            {cancelConfirm.success ? (
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-green-500/20 rounded-full mb-6"><Check size={32} className="text-green-400" /></div>
                <h3 className="text-2xl font-bold text-white mb-3">Subscription Cancelled</h3>
                <p className="text-gray-400 text-sm mb-6">You'll continue to have access until the end of your billing period. After that, you'll be moved to the free Basic plan.</p>
                <button onClick={() => { setCancelConfirm(null); refreshSubscription(); }} className="w-full bg-green-600 hover:bg-green-500 text-white font-bold py-3 px-6 rounded-lg transition-all">Done</button>
              </div>
            ) : (
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-red-500/20 rounded-full mb-6"><X size={32} className="text-red-400" /></div>
                <h3 className="text-2xl font-bold text-white mb-3">Cancel your subscription?</h3>
                <p className="text-gray-300 mb-4">
                  You're about to cancel your <span className="text-green-400 font-bold">{SUBSCRIPTION_CONSTANTS.getDisplayName(userSubscription?.subscriptionPlan)}</span> subscription.
                </p>
                <div className="bg-gray-900 border border-gray-700 rounded-xl p-4 mb-6 text-left text-sm space-y-3">
                  <div className="flex items-start gap-2 text-gray-300">
                    <Check size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Your access continues until the end of the current billing period</span>
                  </div>
                  <div className="flex items-start gap-2 text-gray-300">
                    <Check size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
                    <span>After that, you'll be moved to the free Basic plan</span>
                  </div>
                  <div className="flex items-start gap-2 text-gray-300">
                    <Check size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
                    <span>You can re-subscribe anytime — billing resumes on the next cycle</span>
                  </div>
                </div>
                <div className="flex gap-3">
                  <button onClick={() => setCancelConfirm(null)} className="flex-1 bg-gray-700 hover:bg-gray-600 text-white font-bold py-3 px-6 rounded-lg transition-all border border-gray-600">Keep Plan</button>
                  <button onClick={handleCancelSubscription} disabled={processing === 'cancel'} className="flex-1 bg-red-600 hover:bg-red-500 text-white font-bold py-3 px-6 rounded-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50">
                    {processing === 'cancel' ? <><Loader className="animate-spin h-5 w-5" /> Cancelling...</> : 'Confirm Cancel'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SelectPlan;
